import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import SuggestionChip from "./SuggestionChip";
import BrandQuoteCard from "./BrandQuoteCard";
import { getSeeMoreChips, type ChipDef } from "@/constants/chipPools";
import { trackFeature } from "@/lib/analytics";

interface SeeMoreChipsProps {
  shownLabels: Set<string>;
  currentMealChip: ChipDef;
  onSendMessage: (prompt: string, label?: string) => void;
  favoriteSet?: Set<string>;
  onToggleFavorite?: (label: string) => void;
}

const SeeMoreChips = ({
  shownLabels,
  currentMealChip,
  onSendMessage,
  favoriteSet,
  onToggleFavorite,
}: SeeMoreChipsProps) => {
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo(
    () => getSeeMoreChips(shownLabels, currentMealChip),
    [shownLabels, currentMealChip]
  );

  if (groups.length === 0) return null;

  return (
    <div className="w-full" style={{ marginTop: "12px", marginBottom: "0px" }}>
      <button
        onClick={() => {
          setExpanded((v) => {
            if (!v) trackFeature("chip_see_more");
            return !v;
          });
        }}
        className="mx-auto flex items-center gap-1 font-body text-[13px] font-semibold hover:underline transition-all duration-200 group"
        style={{ color: "#5050C8" }}
      >
        {expanded ? "See less" : "See more ideas"}
        {expanded ? (
          <ChevronUp size={14} className="transition-transform" />
        ) : (
          <ChevronDown size={14} className="transition-transform group-hover:animate-bounce" />
        )}
      </button>

      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{ maxHeight: expanded ? "4000px" : "0px", opacity: expanded ? 1 : 0 }}
      >
        <div className="mt-4" />

        {groups.map((group) => (
          <div key={group.title} className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs">{group.emoji}</span>
              <span
                className="font-body uppercase text-[11px] font-semibold"
                style={{ letterSpacing: "0.5px", color: "#54525C" }}
              >
                {group.title}
              </span>
              <div className="flex-1 h-px bg-accent" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {group.chips.map((chip) => (
                <SuggestionChip
                  key={chip.label}
                  emoji={chip.emoji}
                  label={chip.label}
                  onClick={() => {
                    trackFeature("chip_tap", { chip_label: chip.label, source: "see_more" });
                    const lower = chip.label.toLowerCase();
                    if (lower.includes("vent")) trackFeature("vent_mode", { source: "chip", chip_label: chip.label });
                    if (lower.includes("brain dump")) trackFeature("brain_dump", { source: "chip", chip_label: chip.label });
                    onSendMessage(chip.prompt, chip.label);
                  }}
                  delay={0}
                  isFavorited={favoriteSet?.has(chip.label)}
                  onToggleFavorite={
                    onToggleFavorite ? () => onToggleFavorite(chip.label) : undefined
                  }
                />
              ))}
            </div>
          </div>
        ))}

        <BrandQuoteCard className="animate-stagger-in mt-2 mb-4" />
      </div>
    </div>
  );
};

export default SeeMoreChips;
